import React, { useId } from 'react';
import Header from '@amzn/awsui-components-console/header';
import Table from '@amzn/awsui-components-console/table';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Link from '@amzn/awsui-components-console/link';
import Popover from '@amzn/awsui-components-console/popover';
import Icon from '@amzn/awsui-components-console/icon';
import Skeleton from '@amzn/awsui-components-console/skeleton';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Badge from '@amzn/awsui-components-console/badge';
import { StringVariant } from 'src/generated/graphql';
import { StringVariant as DraftStringVariant } from 'src/features/string-group-detail/types';
import {
  TablePreferences,
  useTablePreferences,
} from 'src/common/ui/TablePreferences';
import { useFollowNavigate } from 'src/common/hooks/useFollowNavigate';
import { variantsTablePreferences } from './stringTablePreferences';
import { useTranslation } from 'src/i18n/shim/useTranslation';

/** A row in the table: a server variant, or one pending in the group draft. */
interface VariantRow {
  cxId: string;
  sourceText: string;
  /** Carried for the Edit action's prefill; not shown as a column. */
  contextNote?: string | null;
  /**
   * True for a variant that exists only in the local group draft. It has no
   * detail page yet — `DescribeString` falls back to the base string for an
   * unknown cxId rather than 404ing — so the row is not a link.
   */
  isPending: boolean;
}

interface VariantsTableProps {
  variants: StringVariant[];
  /**
   * Variants the group draft has but the server does not, from
   * `computePendingVariants`. Rendered after the server ones, unlinked.
   */
  pendingVariants?: DraftStringVariant[];
  /** Base path to construct variant links (adds ?variant={cxId}) */
  baseUrl: string;
  /**
   * Pinned string group version from the current page's ?version= param, if
   * any. Forwarded onto variant links so navigating to a variant preserves
   * the pinned version instead of silently jumping back to latest.
   */
  versionParam?: string;
  /**
   * Whether this view may author a new variant. Same gate the page applies to
   * the source edit — a pinned historical version cannot be branched from.
   */
  canCreateVariant?: boolean;
  /**
   * True while the namespace permission is still in flight, so the control is a
   * skeleton rather than a button claiming a permission that is not yet known.
   */
  permissionsLoading?: boolean;
  /**
   * CX IDs with no variant yet (server list plus the draft's pending ones). An
   * empty list means there is nothing left to create, so the button is disabled
   * rather than opening a dead-end modal.
   */
  availableCxIds?: string[];
  onCreateVariantClick?: () => void;
  /** Opens a server variant for editing; gated like Create Variant. */
  onEditVariantClick?: (variant: {
    cxId: string;
    sourceText: string;
    contextNote?: string | null;
  }) => void;
}

const VariantsTable: React.FC<VariantsTableProps> = ({
  variants,
  pendingVariants = [],
  baseUrl,
  versionParam,
  canCreateVariant = false,
  permissionsLoading = false,
  availableCxIds = [],
  onCreateVariantClick,
  onEditVariantClick,
}) => {
  const { t } = useTranslation();
  // A variant link only changes `?variant=`, so navigating through the router
  // re-renders this page instead of reloading the document.
  const followNavigate = useFollowNavigate();

  const noCxIdsLeft = availableCxIds.length === 0;

  // Server variants first so the stable view does not reorder as drafts come
  // and go; pending ones are appended. CX IDs cannot collide across the two —
  // a pending variant is by definition absent from the server list.
  const rows: VariantRow[] = [
    ...variants.map((v) => ({
      cxId: v.cxId,
      sourceText: v.sourceText,
      contextNote: v.contextNote,
      isPending: false,
    })),
    ...pendingVariants.map((v) => ({
      cxId: v.cxId,
      sourceText: v.sourceText,
      contextNote: v.contextNote,
      isPending: true,
    })),
  ];

  // Per-instance: a module-level id would point every table's button at the
  // first hint if this table were ever rendered twice on a page.
  const unavailableHintId = useId();

  // Columns only: every variant renders, unpaged.
  const { preferences, onConfirm, columnDisplay } = useTablePreferences(
    variantsTablePreferences(t),
  );

  const buildVariantHref = (cxId: string): string => {
    const query = new URLSearchParams();
    query.set('variant', cxId);
    if (versionParam != null) {
      query.set('version', versionParam);
    }
    return `${baseUrl}?${query.toString()}`;
  };

  const renderCreateAction = () => {
    if (permissionsLoading) {
      return <Skeleton display="inline-block" height="32px" width="130px" />;
    }
    if (!canCreateVariant) {
      return undefined;
    }
    return (
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        {noCxIdsLeft && (
          <Box
            variant="small"
            color="text-status-inactive"
            data-testid="create-variant-unavailable"
          >
            {/* `Box`'s own `id` prop is deprecated, so the id goes on an inner
                span — same pattern as SearchTypeToggle's exact-match hint. */}
            <span id={unavailableHintId}>{t('string-variants-all-taken')}</span>
          </Box>
        )}
        <Button
          variant="primary"
          iconName="add-plus"
          disabled={noCxIdsLeft}
          // Only set while the hint is rendered, so it never points at a
          // missing element.
          ariaDescribedby={noCxIdsLeft ? unavailableHintId : undefined}
          onClick={onCreateVariantClick}
          data-testid="create-variant-button"
        >
          {t('string-variants-create')}
        </Button>
      </SpaceBetween>
    );
  };

  return (
    <Table
      columnDisplay={columnDisplay}
      wrapLines={preferences.wrapLines}
      stripedRows={preferences.stripedRows}
      contentDensity={preferences.contentDensity}
      preferences={
        <TablePreferences
          config={variantsTablePreferences(t)}
          preferences={preferences}
          onConfirm={onConfirm}
        />
      }
      header={
        <Header
          counter={rows.length > 0 ? `(${rows.length})` : undefined}
          actions={renderCreateAction()}
          info={
            <Popover
              triggerType="text"
              header={t('string-variants-heading')}
              content={t('string-manage-variants-info')}
              triggerAriaLabel={t('string-manage-variants-info-aria')}
            >
              <Icon name="status-info" size="small" />
            </Popover>
          }
        >
          {t('string-variants-heading')}
        </Header>
      }
      items={rows}
      trackBy="cxId"
      ariaLabels={{ tableLabel: t('string-variants-table-aria') }}
      columnDefinitions={[
        {
          id: 'cxId',
          header: t('string-meta-cx-id'),
          cell: (item) => (
            <span data-testid="variant-row" data-cxid={item.cxId}>
              <SpaceBetween
                direction="horizontal"
                size="xxs"
                alignItems="center"
              >
                <span data-testid="variant-cxid">
                  {item.isPending ? (
                    item.cxId
                  ) : (
                    <Link
                      href={buildVariantHref(item.cxId)}
                      onFollow={followNavigate}
                    >
                      {item.cxId}
                    </Link>
                  )}
                </span>
                {item.isPending && (
                  <Badge color="blue" data-testid="variant-pending-badge">
                    {t('string-variants-new-pending')}
                  </Badge>
                )}
              </SpaceBetween>
            </span>
          ),
        },
        {
          id: 'sourceText',
          header: t('string-column-variant-source-text'),
          cell: (item) => (
            <span data-testid="variant-row-source-text">{item.sourceText}</span>
          ),
        },
        {
          id: 'actions',
          header: t('string-column-actions'),
          // Pending rows are amended on the group page.
          cell: (item) => {
            if (item.isPending) {
              return null;
            }
            if (permissionsLoading) {
              return (
                <Skeleton display="inline-block" height="32px" width="60px" />
              );
            }
            if (!canCreateVariant) {
              return null;
            }
            return (
              <Button
                onClick={() =>
                  onEditVariantClick?.({
                    cxId: item.cxId,
                    sourceText: item.sourceText,
                    contextNote: item.contextNote,
                  })
                }
                data-testid="variant-edit-button"
              >
                {t('string-edit')}
              </Button>
            );
          },
        },
      ]}
      empty={
        <Box textAlign="center" color="text-status-inactive" padding="l">
          {t('string-variants-empty')}
        </Box>
      }
    />
  );
};

export default VariantsTable;
